import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStatement } from '../hooks/useStatement';
import { StatementInvoice } from '../model';
import { CollectionInvoicePdf } from '../../clients/model';
import StatementInvoicesTable from './StatementInvoicesTable';

interface Props {
  open: boolean;
  onClose: () => void;
  acctNo: string;
  company: string;
  location: string;
  onPdfUploaded: () => void;
}

const StatementPdfsModal: React.FC<Props> = ({ open, onClose, acctNo, company, location, onPdfUploaded }) => {
  const { data, isLoading, isError } = useStatement(acctNo, location, open);
  const [invoices, setInvoices] = useState<StatementInvoice[]>([]);

  useEffect(() => {
    if (data?.data) setInvoices(data.data.invoices);
  }, [data]);

  const matched = invoices.filter((i) => i.has_pdf).length;

  const handleUploaded = (applyTo: string, pdf: CollectionInvoicePdf) => {
    setInvoices((prev) => prev.map((i) => (i.apply_to === applyTo ? { ...i, has_pdf: true, pdf } : i)));
    onPdfUploaded();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Statement Invoices — {company}</Typography>
          <Typography variant="body2" color="textSecondary">
            {matched} of {invoices.length} {invoices.length === 1 ? 'invoice has a PDF' : 'invoices have PDFs'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">Failed to load statement invoices.</Alert>
          </Box>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <StatementInvoicesTable
              invoices={invoices}
              location={location}
              acctNo={acctNo}
              onUploaded={handleUploaded}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatementPdfsModal;
